import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
    Alert
} from '@mui/material';
import { IconPlus, IconTrash, IconX, IconArrowLeft, IconUpload, IconSend } from '@tabler/icons';
import api from '../../../../services/api';
import TemplatePreviewCard from './TemplatePreviewCard';

const LANGUAGE_OPTIONS = [
    { value: 'pt_BR', label: 'Português (BR)' },
    { value: 'en_US', label: 'Inglês (EUA)' },
    { value: 'es_ES', label: 'Espanhol' }
];

const CATEGORY_OPTIONS = [
    { value: 'MARKETING', label: 'Marketing' },
    { value: 'UTILITY', label: 'Utilidade' },
    { value: 'AUTHENTICATION', label: 'Autenticação' }
];

const DEFAULT_TEMPLATE = {
    integration: '',
    name: '',
    language: 'pt_BR',
    category: 'UTILITY',
    header_format: 'TEXT',
    header_text: '',
    header_example_value: '',
    body_text: '',
    body_example_values: [],
    body_text_variations: [],
    footer_text: '',
    buttons: [],
    delay_message: 15
};

const WAPI_BUTTON_TYPE_OPTIONS = [
    { value: 'REPLAY', label: 'Resposta rápida' },
    { value: 'CALL', label: 'Ligar' },
    { value: 'URL', label: 'Abrir link' }
];

const OFFICIAL_BUTTON_TYPE_OPTIONS = [
    { value: 'QUICK_REPLY', label: 'Resposta rápida' },
    { value: 'URL', label: 'Abrir link' },
    { value: 'PHONE_NUMBER', label: 'Ligar' }
];

// Limites reais da Meta pra botão de Template Message - vale pros dois
// client_type (a API não-oficial não impõe isso sozinha, mas o número
// pode migrar pra API Oficial um dia, e a Meta rejeitaria de qualquer
// forma). Mesmos valores de `WHATSAPP_TEMPLATE_MAX_BUTTONS`/
// `WHATSAPP_TEMPLATE_BUTTON_MAX_CHARS` em `conversas/views.py`.
const TEMPLATE_MAX_BUTTONS = 3;
const TEMPLATE_BUTTON_MAX_CHARS = 21;
// Só pra API não-oficial - até 2 variações ADICIONAIS de `body_text`.
const TEMPLATE_MAX_VARIATIONS = 2;
// Limite real da w-api pro parâmetro `delayMessage` (delay de "digitando..."
// simulado ANTES de cada mensagem - nada a ver com o intervalo entre
// disparos da campanha, que é `Campaign.send_delay_seconds`). Mesmos
// valores de `WHATSAPP_TEMPLATE_DELAY_MESSAGE_MIN/MAX` em `conversas/views.py`.
const TEMPLATE_DELAY_MESSAGE_MIN = 1;
const TEMPLATE_DELAY_MESSAGE_MAX = 15;

const countVariables = (text) => (text.match(/\{\{\d+\}\}/g) || []).length;

const ViewWhatsappTemplates = () => {
    const [appId, setAppId] = useState(null);
    const [integrations, setIntegrations] = useState([]);
    const [selectedIntegration, setSelectedIntegration] = useState(null);

    const [metaTemplates, setMetaTemplates] = useState([]);
    const [localTemplates, setLocalTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [loadError, setLoadError] = useState('');

    const [openDialog, setOpenDialog] = useState(false);
    const [form, setForm] = useState(DEFAULT_TEMPLATE);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);
    const [headerImageFile, setHeaderImageFile] = useState(null);
    const [headerImagePreviewUrl, setHeaderImagePreviewUrl] = useState(null);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadIntegrations();
    }, [appId]);

    // Revoga a URL local do preview de imagem escolhida - senão o browser
    // mantém o blob na memória mesmo depois de trocar/fechar o diálogo.
    useEffect(() => {
        return () => {
            if (headerImagePreviewUrl) URL.revokeObjectURL(headerImagePreviewUrl);
        };
    }, [headerImagePreviewUrl]);

    const loadIntegrations = async () => {
        const res = await api.get(`v1/apps/${appId}/integrations/`);
        setIntegrations((res.data.integrations || res.data || []).filter((i) => i.client_type === 'official' || i.client_type === 'w_api'));
    };

    const loadTemplatesForIntegration = async (integrationId, clientType) => {
        setLoadingTemplates(true);
        setLoadError('');
        try {
            // W-API não tem conta na Meta pra consultar - os templates dela
            // só existem guardados localmente no próprio CRM.
            const requests = [api.get(`v1/apps/${appId}/whatsapp_templates/?integration_id=${integrationId}`)];
            if (clientType === 'official') {
                requests.unshift(api.get(`v1/apps/${appId}/integrations/${integrationId}/whatsapp_templates_meta/`));
            }

            const results = await Promise.all(requests);
            const localRes = results[results.length - 1];
            const metaRes = clientType === 'official' ? results[0] : null;

            setMetaTemplates(metaRes && Array.isArray(metaRes.data) ? metaRes.data : []);
            setLocalTemplates(localRes.data || []);
        } catch (err) {
            setMetaTemplates([]);
            setLoadError(err.response?.data?.error || 'Erro ao consultar os templates.');
        } finally {
            setLoadingTemplates(false);
        }
    };

    const handleSelectIntegration = (integration) => {
        setSelectedIntegration(integration);
        loadTemplatesForIntegration(integration.id, integration.client_type);
    };

    const handleChangeIntegration = () => {
        setSelectedIntegration(null);
        setMetaTemplates([]);
        setLocalTemplates([]);
        setLoadError('');
    };

    const refreshStatus = async (templateId) => {
        try {
            await api.post(`v1/apps/${appId}/whatsapp_templates/${templateId}/refresh_status/`);
            loadTemplatesForIntegration(selectedIntegration.id, selectedIntegration.client_type);
        } catch (err) {
            setLoadError(err.response?.data?.error || 'Erro ao atualizar o status do template.');
        }
    };

    const deleteTemplate = async (templateId) => {
        await api.delete(`v1/apps/${appId}/whatsapp_templates/${templateId}/`);
        loadTemplatesForIntegration(selectedIntegration.id, selectedIntegration.client_type);
    };

    // Template criado direto no Meta Business Manager (nunca passou pelo
    // CRM) - cria a linha local espelhando o que já existe na Meta, sem
    // recriar nada lá. Sem isso, ele não pode ser usado em Campanhas (que
    // só referenciam um WhatsappTemplate local).
    const importTemplate = async (metaTemplate) => {
        try {
            await api.post(`v1/apps/${appId}/integrations/${selectedIntegration.id}/whatsapp_templates_meta/import/`, {
                name: metaTemplate.name,
                language: metaTemplate.language
            });
            loadTemplatesForIntegration(selectedIntegration.id, selectedIntegration.client_type);
        } catch (err) {
            setLoadError(err.response?.data?.error || 'Erro ao importar o template.');
        }
    };

    const openCreateDialog = () => {
        setForm({ ...DEFAULT_TEMPLATE, integration: selectedIntegration?.id || '' });
        setError('');
        setHeaderImageFile(null);
        setHeaderImagePreviewUrl(null);
        setOpenDialog(true);
    };

    const bodyVarsCount = countVariables(form.body_text);
    const headerHasVariable = countVariables(form.header_text) > 0;
    const formIntegration = integrations.find((i) => i.id === form.integration);
    const isWApiForm = formIntegration?.client_type === 'w_api';

    const handleHeaderImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (headerImagePreviewUrl) URL.revokeObjectURL(headerImagePreviewUrl);
        setHeaderImageFile(file);
        setHeaderImagePreviewUrl(URL.createObjectURL(file));
        // Pro formulário não-oficial não existe seletor de "Tipo de header"
        // (só a Oficial tem) - marca `header_format` direto aqui, senão a
        // prévia nunca reconhece que virou uma imagem.
        if (isWApiForm) setForm((prev) => ({ ...prev, header_format: 'IMAGE' }));
    };

    const save = async () => {
        setSaving(true);
        setError('');
        try {
            const formData = new FormData();
            formData.append('integration', form.integration);
            formData.append('name', form.name);
            formData.append('body_text', form.body_text);
            formData.append('body_example_values', JSON.stringify(form.body_example_values));
            formData.append('buttons', JSON.stringify(form.buttons));

            // Imagem de cabeçalho (opcional) vale pros dois client_type -
            // só o upload resumível de 2 etapas na Meta (feito do lado do
            // backend, pra aprovação) é exclusivo da API Oficial. A API
            // não-oficial não tem conceito de header de TEXTO separado
            // (só a mensagem em si, `body_text`) - por isso só manda
            // `header_image` quando tem arquivo escolhido, nunca
            // `header_text`.
            if (headerImageFile) formData.append('header_image', headerImageFile);

            if (isWApiForm) {
                formData.append('delay_message', form.delay_message);
                formData.append('body_text_variations', JSON.stringify(form.body_text_variations));
            } else {
                formData.append('language', form.language);
                formData.append('category', form.category);
                formData.append('footer_text', form.footer_text);
                if (form.header_format === 'TEXT') {
                    formData.append('header_text', form.header_text);
                    formData.append('header_example_value', form.header_example_value);
                }
            }

            const res = await api.post(`v1/apps/${appId}/whatsapp_templates/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            if (res.data.error) {
                setError(res.data.error);
            } else {
                setOpenDialog(false);
                loadTemplatesForIntegration(selectedIntegration.id, selectedIntegration.client_type);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Erro ao criar template.');
        } finally {
            setSaving(false);
        }
    };

    const buttonTypeOptions = isWApiForm ? WAPI_BUTTON_TYPE_OPTIONS : OFFICIAL_BUTTON_TYPE_OPTIONS;
    const buttonTextKey = isWApiForm ? 'buttonText' : 'text';
    const buttonShowsPhone = (type) => (isWApiForm ? type === 'CALL' : type === 'PHONE_NUMBER');
    const buttonPhoneKey = isWApiForm ? 'phone' : 'phone_number';
    const buttonShowsUrl = (type) => type === 'URL';

    const addButton = () => {
        const newButton = { type: buttonTypeOptions[0].value, [buttonTextKey]: '' };
        setForm({ ...form, buttons: [...form.buttons, newButton] });
    };
    const updateButton = (i, patch) => {
        const buttons = [...form.buttons];
        buttons[i] = { ...buttons[i], ...patch };
        setForm({ ...form, buttons });
    };
    const removeButton = (i) => setForm({ ...form, buttons: form.buttons.filter((_, idx) => idx !== i) });

    // Junta os templates da Meta (fonte principal) com os locais - só pra
    // saber qual tem um `id` local (habilita Atualizar status/Excluir) e
    // pra não perder de vista um template que falhou ao criar (nunca
    // chegou a existir na Meta, então não aparece na lista dela sozinho).
    const localByKey = {};
    localTemplates.forEach((t) => {
        localByKey[`${t.name}__${t.language}`] = t;
    });
    const matchedKeys = new Set();
    const entriesFromMeta = metaTemplates.map((mt) => {
        const key = `${mt.name}__${mt.language}`;
        const local = localByKey[key] || null;
        if (local) matchedKeys.add(key);
        return { meta: mt, local };
    });
    const entriesLocalOnly = localTemplates
        .filter((t) => !matchedKeys.has(`${t.name}__${t.language}`) && !t.meta_template_id)
        .map((t) => ({ meta: null, local: t }));
    const displayEntries = [...entriesFromMeta, ...entriesLocalOnly];

    const draftEntry = { meta: null, local: { ...form, status: 'local' } };

    return (
        <Box p={4} sx={{ minHeight: '100vh' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Box>
                    <Typography variant="h4" fontWeight="700">
                        Templates do WhatsApp
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {selectedIntegration
                            ? selectedIntegration.client_type === 'official'
                                ? `Templates da conta "${selectedIntegration.displayName}", consultados direto na Meta`
                                : `Templates salvos no CRM pra "${selectedIntegration.displayName}" (WhatsApp não oficial)`
                            : 'Selecione a integração pra ver os templates dessa conta'}
                    </Typography>
                </Box>

                {selectedIntegration && (
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" onClick={handleChangeIntegration}>
                            Trocar integração
                        </Button>
                        <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={openCreateDialog}>
                            Criar Template
                        </Button>
                    </Stack>
                )}
            </Stack>

            {!selectedIntegration && (
                <Grid container spacing={2}>
                    {integrations.map((integration) => (
                        <Grid item xs={12} sm={6} md={4} key={integration.id}>
                            <Card sx={{ borderRadius: 3, cursor: 'pointer' }} onClick={() => handleSelectIntegration(integration)}>
                                <CardContent>
                                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                                        <Typography variant="subtitle1" fontWeight="700">
                                            {integration.displayName}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            label={integration.client_type === 'official' ? 'Oficial' : 'Não Oficial'}
                                            color={integration.client_type === 'official' ? 'success' : 'default'}
                                        />
                                    </Stack>
                                    {integration.phoneNumber && (
                                        <Typography variant="body2" color="text.secondary">
                                            {integration.phoneNumber}
                                        </Typography>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {integrations.length === 0 && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="text.secondary">
                                Nenhuma integração WhatsApp Oficial ou Não Oficial cadastrada ainda.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            )}

            {selectedIntegration && (
                <>
                    {loadError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {loadError}
                        </Alert>
                    )}

                    {loadingTemplates ? (
                        <Box display="flex" justifyContent="center" py={4}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {displayEntries.map((entry) => (
                                <Grid item xs={12} sm={6} md={4} key={entry.meta?.id || entry.local?.id}>
                                    <TemplatePreviewCard
                                        appId={appId}
                                        entry={entry}
                                        clientType={selectedIntegration.client_type}
                                        onRefreshStatus={refreshStatus}
                                        onDelete={deleteTemplate}
                                        onImport={importTemplate}
                                    />
                                </Grid>
                            ))}
                            {displayEntries.length === 0 && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="text.secondary">
                                        Nenhum template encontrado pra essa conta.
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </>
            )}

            {/* CRIAR TEMPLATE - tela cheia, formulário + prévia ao vivo */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullScreen>
                <Box sx={{ display: 'flex', height: '100%', background: '#f8fafc' }}>
                    <Box sx={{ flex: 1, overflow: 'auto', p: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                            <Box>
                                <Button
                                    variant="outlined"
                                    startIcon={<IconArrowLeft size={18} />}
                                    onClick={() => setOpenDialog(false)}
                                    sx={{ borderRadius: '12px', textTransform: 'none' }}
                                >
                                    Voltar
                                </Button>
                                <Typography variant="h3" sx={{ fontWeight: 900, mt: 2 }}>
                                    Criar Template
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    {isWApiForm
                                        ? 'Mensagem + botões guardados no CRM, reutilizáveis em disparos (WhatsApp não oficial)'
                                        : 'Enviado pra aprovação da Meta - depois de aprovado, fica disponível pra disparos'}
                                </Typography>
                            </Box>
                            <IconButton onClick={() => setOpenDialog(false)}>
                                <IconX />
                            </IconButton>
                        </Box>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Integração"
                                    value={form.integration}
                                    onChange={(e) => setForm({ ...form, integration: e.target.value })}
                                >
                                    {integrations.map((i) => (
                                        <MenuItem key={i.id} value={i.id}>
                                            {i.displayName} {i.client_type === 'w_api' ? '(Não Oficial)' : '(Oficial)'}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label="Nome do template"
                                    helperText="minúsculo, sem espaço (ex: boas_vindas)"
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                />
                            </Grid>

                            {!isWApiForm && (
                                <>
                                    <Grid item xs={6} md={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Idioma"
                                            value={form.language}
                                            onChange={(e) => setForm({ ...form, language: e.target.value })}
                                        >
                                            {LANGUAGE_OPTIONS.map((o) => (
                                                <MenuItem key={o.value} value={o.value}>
                                                    {o.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={6} md={3}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Categoria"
                                            value={form.category}
                                            onChange={(e) => setForm({ ...form, category: e.target.value })}
                                        >
                                            {CATEGORY_OPTIONS.map((o) => (
                                                <MenuItem key={o.value} value={o.value}>
                                                    {o.label}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            select
                                            fullWidth
                                            label="Tipo de header"
                                            value={form.header_format}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    header_format: e.target.value,
                                                    header_text: '',
                                                    header_example_value: ''
                                                })
                                            }
                                        >
                                            <MenuItem value="TEXT">Texto</MenuItem>
                                            <MenuItem value="IMAGE">Imagem</MenuItem>
                                        </TextField>
                                    </Grid>

                                    {form.header_format === 'TEXT' ? (
                                        <>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Header (opcional)"
                                                    helperText="Pode ter no máximo 1 variável: {{1}}"
                                                    value={form.header_text}
                                                    onChange={(e) => setForm({ ...form, header_text: e.target.value })}
                                                />
                                            </Grid>

                                            {headerHasVariable && (
                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        label="Exemplo da variável do header"
                                                        value={form.header_example_value}
                                                        onChange={(e) => setForm({ ...form, header_example_value: e.target.value })}
                                                    />
                                                </Grid>
                                            )}
                                        </>
                                    ) : (
                                        <Grid item xs={12}>
                                            <Button variant="outlined" component="label" startIcon={<IconUpload size={18} />}>
                                                {headerImageFile ? headerImageFile.name : 'Selecionar imagem'}
                                                <input type="file" hidden accept="image/*" onChange={handleHeaderImageChange} />
                                            </Button>
                                        </Grid>
                                    )}
                                </>
                            )}

                            {/* API não-oficial não tem conceito de header de TEXTO separado (só a
                                mensagem em si) - mas ganha imagem de cabeçalho opcional, igual à
                                Oficial, sem precisar do seletor de tipo (a Meta exige declarar o
                                formato do template antecipadamente pra aprovação; a w-api não tem
                                esse conceito, então "tem imagem ou não" já basta). */}
                            {isWApiForm && (
                                <Grid item xs={12}>
                                    <Button variant="outlined" component="label" startIcon={<IconUpload size={18} />}>
                                        {headerImageFile ? headerImageFile.name : 'Adicionar imagem de cabeçalho (opcional)'}
                                        <input type="file" hidden accept="image/*" onChange={handleHeaderImageChange} />
                                    </Button>
                                    {headerImageFile && (
                                        <Button
                                            size="small"
                                            color="error"
                                            sx={{ ml: 1 }}
                                            onClick={() => {
                                                if (headerImagePreviewUrl) URL.revokeObjectURL(headerImagePreviewUrl);
                                                setHeaderImageFile(null);
                                                setHeaderImagePreviewUrl(null);
                                                setForm((prev) => ({ ...prev, header_format: 'TEXT' }));
                                            }}
                                        >
                                            Remover
                                        </Button>
                                    )}
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label={isWApiForm ? 'Mensagem' : 'Corpo da mensagem'}
                                    helperText="Use {{1}}, {{2}}... pras variáveis (ex: Olá {{1}}, seu pedido {{2}} foi atualizado)"
                                    value={form.body_text}
                                    onChange={(e) => setForm({ ...form, body_text: e.target.value })}
                                />
                            </Grid>

                            {bodyVarsCount > 0 && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" mb={1}>
                                        {isWApiForm
                                            ? 'Exemplos das variáveis do corpo (só pra você conferir na prévia - o valor de verdade é preenchido por destinatário na hora do disparo):'
                                            : 'Exemplos das variáveis do corpo (exigido pela Meta pra aprovação):'}
                                    </Typography>
                                    <Grid container spacing={1}>
                                        {Array.from({ length: bodyVarsCount }).map((_, i) => (
                                            <Grid item xs={6} key={i}>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    label={`Exemplo {{${i + 1}}}`}
                                                    value={form.body_example_values[i] || ''}
                                                    onChange={(e) => {
                                                        const values = [...form.body_example_values];
                                                        values[i] = e.target.value;
                                                        setForm({ ...form, body_example_values: values });
                                                    }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Grid>
                            )}

                            {!isWApiForm && (
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        label="Footer (opcional)"
                                        helperText="Sem variáveis (regra da Meta)"
                                        value={form.footer_text}
                                        onChange={(e) => setForm({ ...form, footer_text: e.target.value })}
                                    />
                                </Grid>
                            )}

                            {isWApiForm && (
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Delay de digitação antes desta mensagem (segundos)"
                                        helperText="Entre 1 e 15 segundos (limite da api não oficial) - só o tempo de 'digitando...' simulado antes dela chegar. Não é o intervalo entre um disparo e outro da campanha (isso você escolhe na tela de Marketing)."
                                        inputProps={{ min: TEMPLATE_DELAY_MESSAGE_MIN, max: TEMPLATE_DELAY_MESSAGE_MAX }}
                                        value={form.delay_message}
                                        onChange={(e) => setForm({ ...form, delay_message: Number(e.target.value) })}
                                        onBlur={() =>
                                            setForm((prev) => ({
                                                ...prev,
                                                delay_message: Math.min(
                                                    TEMPLATE_DELAY_MESSAGE_MAX,
                                                    Math.max(
                                                        TEMPLATE_DELAY_MESSAGE_MIN,
                                                        Number(prev.delay_message) || TEMPLATE_DELAY_MESSAGE_MIN
                                                    )
                                                )
                                            }))
                                        }
                                    />
                                </Grid>
                            )}

                            {/* VARIAÇÕES DE TEXTO - só API não-oficial */}
                            {isWApiForm && (
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                        <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                            Variações de texto (opcional)
                                        </Typography>
                                        {form.body_text_variations.length < TEMPLATE_MAX_VARIATIONS && (
                                            <Button
                                                startIcon={<IconPlus size={16} />}
                                                sx={{ textTransform: 'none' }}
                                                onClick={() =>
                                                    setForm({ ...form, body_text_variations: [...form.body_text_variations, ''] })
                                                }
                                            >
                                                Adicionar variação
                                            </Button>
                                        )}
                                    </Box>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        A Meta detecta padrão de envio em massa quando a mensagem é sempre IDÊNTICA - crie pelo menos 2-3
                                        versões diferentes (troque a saudação, a pontuação, use um emoji diferente ou reordene as frases,
                                        mantendo o mesmo sentido) e o disparo sorteia uma delas por destinatário. Totalmente opcional, mas
                                        reduz bastante o risco de bloqueio do número.
                                    </Alert>
                                    <Stack spacing={2}>
                                        {form.body_text_variations.map((variacao, i) => (
                                            <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                                                <TextField
                                                    fullWidth
                                                    multiline
                                                    minRows={2}
                                                    size="small"
                                                    label={`Variação ${i + 2}`}
                                                    value={variacao}
                                                    onChange={(e) => {
                                                        const variations = [...form.body_text_variations];
                                                        variations[i] = e.target.value;
                                                        setForm({ ...form, body_text_variations: variations });
                                                    }}
                                                />
                                                <IconButton
                                                    color="error"
                                                    onClick={() =>
                                                        setForm({
                                                            ...form,
                                                            body_text_variations: form.body_text_variations.filter((_, idx) => idx !== i)
                                                        })
                                                    }
                                                >
                                                    <IconTrash size={18} />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Stack>
                                </Grid>
                            )}

                            {/* BOTÕES */}
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                        Botões ({form.buttons.length}/{TEMPLATE_MAX_BUTTONS})
                                    </Typography>
                                    <Button
                                        startIcon={<IconPlus size={16} />}
                                        onClick={addButton}
                                        disabled={form.buttons.length >= TEMPLATE_MAX_BUTTONS}
                                        sx={{ textTransform: 'none' }}
                                    >
                                        Adicionar botão
                                    </Button>
                                </Box>

                                {isWApiForm && (
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Dica: termine sempre com um botão tipo "Não tenho interesse" - deixa claro pra Meta que o cliente
                                        tem uma saída fácil, o que ajuda a manter uma taxa de bloqueio/denúncia baixa e preserva a saúde do
                                        número.
                                    </Alert>
                                )}

                                <Stack spacing={2}>
                                    {form.buttons.map((btn, i) => (
                                        <Card key={i} sx={{ p: 2, borderRadius: '18px' }}>
                                            <Grid container spacing={2}>
                                                <Grid item xs={12} md={3}>
                                                    <TextField
                                                        select
                                                        fullWidth
                                                        size="small"
                                                        label="Tipo"
                                                        value={btn.type}
                                                        onChange={(e) => updateButton(i, { type: e.target.value })}
                                                    >
                                                        {buttonTypeOptions.map((o) => (
                                                            <MenuItem key={o.value} value={o.value}>
                                                                {o.label}
                                                            </MenuItem>
                                                        ))}
                                                    </TextField>
                                                </Grid>

                                                <Grid item xs={12} md={buttonShowsPhone(btn.type) || buttonShowsUrl(btn.type) ? 4 : 8}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Texto do botão"
                                                        inputProps={{ maxLength: TEMPLATE_BUTTON_MAX_CHARS }}
                                                        helperText={`${(btn[buttonTextKey] || '').length}/${TEMPLATE_BUTTON_MAX_CHARS}`}
                                                        value={btn[buttonTextKey] || ''}
                                                        onChange={(e) => updateButton(i, { [buttonTextKey]: e.target.value })}
                                                    />
                                                </Grid>

                                                {buttonShowsPhone(btn.type) && (
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="Telefone"
                                                            value={btn[buttonPhoneKey] || ''}
                                                            onChange={(e) => updateButton(i, { [buttonPhoneKey]: e.target.value })}
                                                        />
                                                    </Grid>
                                                )}

                                                {buttonShowsUrl(btn.type) && (
                                                    <Grid item xs={12} md={4}>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            label="URL"
                                                            value={btn.url || ''}
                                                            onChange={(e) => updateButton(i, { url: e.target.value })}
                                                        />
                                                    </Grid>
                                                )}

                                                <Grid item xs={12} md={1}>
                                                    <Button
                                                        color="error"
                                                        onClick={() => removeButton(i)}
                                                        sx={{ height: '100%', minWidth: '100%' }}
                                                    >
                                                        <IconTrash size={18} />
                                                    </Button>
                                                </Grid>
                                            </Grid>
                                        </Card>
                                    ))}
                                </Stack>
                            </Grid>
                        </Grid>
                    </Box>

                    {/* PRÉVIA AO VIVO */}
                    <Box sx={{ width: 420, borderLeft: '1px solid rgba(15,23,42,0.06)', background: '#fff', p: 3, overflow: 'auto' }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 3 }}>
                            Pré-visualização
                        </Typography>

                        <TemplatePreviewCard appId={appId} entry={draftEntry} localImagePreviewUrl={headerImagePreviewUrl} />

                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <IconSend size={18} />}
                            disabled={saving || !form.name || !form.body_text || !form.integration}
                            onClick={save}
                            sx={{ mt: 3, height: 54, borderRadius: '16px', textTransform: 'none', fontWeight: 700 }}
                        >
                            {isWApiForm ? 'Salvar template' : 'Enviar pra aprovação'}
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default ViewWhatsappTemplates;
